import React from 'react';
import {Box, Tooltip, Typography} from '@mui/material';
import {NavLink, useLocation} from 'react-router-dom';
import {useResourceDefinitions, useCreatePath, useTranslate} from 'react-admin';
import {Dashboard as DashboardIcon} from '@mui/icons-material';
import {t} from '../../themes/statamicTokens';
import {useChromeMode} from '../../themes/ThemeModeContext';

const GROUPS = [
    {
        label: 'Organizations',
        names: ['umbrellas', 'umbrellaAdmins', 'charities', 'volunteers'],
    },
    {
        label: 'People',
        names: ['children', 'guardians'],
    },
    {
        label: 'Content',
        names: ['posts', 'posts-pending', 'childUpdatePostStats'],
    },
    {
        label: 'Communication',
        names: ['mailSettings', 'mailLogs', 'activities'],
    },
    {
        label: 'System',
        names: ['exportSettings', 'metadata-definitions', 'rnActions', 'errors', 'userActionLogs'],
    },
];

const MenuItem = ({to, icon: Icon, label}) => {
    const {pathname} = useLocation();
    const {chrome, sidebarCollapsed} = useChromeMode();
    const active = pathname === to || pathname.startsWith(`${to}/`);
    const textColor = active ? chrome.sidebarTextActive : chrome.sidebarText;
    const iconColor = active ? chrome.sidebarIconActive : chrome.sidebarIcon;

    const row = (
        <NavLink
            to={to}
            style={{
                textDecoration: 'none',
                color: 'inherit',
                display: 'block',
                outline: 'none',
                WebkitTapHighlightColor: 'transparent',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                    gap: sidebarCollapsed ? 0 : 1.25,
                    height: 34,
                    px: sidebarCollapsed ? 0 : 1.25,
                    mx: sidebarCollapsed ? 0.5 : 1,
                    borderRadius: `${t.radius.sm}px`,
                    fontSize: `${t.type.small.size}px`,
                    fontWeight: active ? 600 : 500,
                    color: textColor,
                    backgroundColor: active ? chrome.sidebarItemActive : 'transparent',
                    position: 'relative',
                    transition: 'background-color 120ms ease, color 120ms ease',
                    '&:hover': {
                        backgroundColor: active ? chrome.sidebarItemActive : chrome.sidebarItemHover,
                        color: chrome.sidebarTextHover,
                    },
                    '&:hover .sidebar-icon-slot': {
                        color: chrome.sidebarIconActive,
                    },
                    '&:hover .sidebar-icon-slot svg path, &:hover .sidebar-icon-slot svg circle, &:hover .sidebar-icon-slot svg rect, &:hover .sidebar-icon-slot svg polygon, &:hover .sidebar-icon-slot svg ellipse': {
                        fill: `${chrome.sidebarIconActive} !important`,
                    },
                    '&::before': active && !sidebarCollapsed ? {
                        content: '""',
                        position: 'absolute',
                        left: -4,
                        top: 6,
                        bottom: 6,
                        width: 3,
                        borderRadius: 2,
                        backgroundColor: t.color.accent.primary,
                    } : {},
                }}
            >
                <Box
                    className="sidebar-icon-slot"
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 20,
                        height: 20,
                        flexShrink: 0,
                        color: iconColor,
                        '& .MuiSvgIcon-root': {
                            fontSize: 18,
                            color: `${iconColor} !important`,
                        },
                        '& svg': {
                            width: 18,
                            height: 18,
                            color: iconColor,
                        },
                        '& svg path, & svg circle, & svg rect, & svg polygon, & svg ellipse': {
                            fill: `${iconColor} !important`,
                        },
                    }}
                >
                    {Icon ? <Icon/> : null}
                </Box>
                {!sidebarCollapsed && (
                    <Box sx={{flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                        {label}
                    </Box>
                )}
            </Box>
        </NavLink>
    );

    if (sidebarCollapsed) {
        return (
            <Tooltip title={label} placement="right" disableInteractive>
                {row}
            </Tooltip>
        );
    }
    return row;
};

const GroupHeading = ({children}) => {
    const {chrome, sidebarCollapsed} = useChromeMode();
    if (sidebarCollapsed) {
        return (
            <Box
                sx={{
                    mx: 1.5,
                    mt: 1.5,
                    mb: 0.75,
                    height: '1px',
                    backgroundColor: chrome.sidebarDivider,
                }}
            />
        );
    }
    return (
        <Typography
            sx={{
                px: 2,
                mt: 2.5,
                mb: 0.75,
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: chrome.sidebarHeading,
            }}
        >
            {children}
        </Typography>
    );
};

const StatamicMenu = () => {
    const resources = useResourceDefinitions();
    const createPath = useCreatePath();
    const translate = useTranslate();
    const {sidebarCollapsed} = useChromeMode();
    const available = Object.values(resources).filter(r => r.hasList);
    const byName = Object.fromEntries(available.map(r => [r.name, r]));

    const assigned = new Set();
    const renderedGroups = GROUPS.map(group => {
        const items = group.names
            .map(n => byName[n])
            .filter(Boolean);
        items.forEach(r => assigned.add(r.name));
        return {label: group.label, items};
    }).filter(g => g.items.length > 0);

    const other = available.filter(r => !assigned.has(r.name));
    if (other.length > 0) {
        renderedGroups.push({label: 'Other', items: other});
    }

    return (
        <Box
            component="nav"
            sx={{
                display: 'flex',
                flexDirection: 'column',
                pt: 1,
                pb: 3,
            }}
        >
            <Box sx={{px: sidebarCollapsed ? 0 : 1, pt: 1}}>
                <MenuItem to="/" icon={DashboardIcon} label={translate('Dashboard')}/>
            </Box>

            {renderedGroups.map(group => (
                <Box key={group.label}>
                    <GroupHeading>{translate(group.label)}</GroupHeading>
                    {group.items.map(r => (
                        <MenuItem
                            key={r.name}
                            to={createPath({resource: r.name, type: 'list'})}
                            icon={r.icon}
                            label={translate(r.options?.label ?? r.name)}
                        />
                    ))}
                </Box>
            ))}
        </Box>
    );
};

export default StatamicMenu;
